import { FontWeightType } from 'components/VisualStudioPlayground/VisualEditor/Types'
import FontFaceObserver from 'fontfaceobserver'
import { makeAutoObservable, observable } from 'mobx'
import opentype from 'opentype.js'
import { IFont, LoadingState } from 'stores/IFont'
import { reportError } from 'utils/error'

export type IBlazeFont = {
  family: string
  variants: Array<string>
  files: Record<string, string>
  styles: string
}

const FONT_LOADING_TIMEOUT = 30000 // 30s

export class BlazeFont implements IFont {
  name: IFont['name']
  family: IFont['family']
  variants: IFont['variants']
  loadingState: IFont['loadingState'] = 'idle'
  loadingPromise?: IFont['loadingPromise']
  files: Record<string, string>
  styles: string

  analyses = observable.map<string, opentype.Font>()

  constructor(font: IBlazeFont) {
    makeAutoObservable(this)

    this.name = font.family
    this.family = font.family
    this.variants = font.variants
    this.files = font.files
    this.styles = font.styles
  }

  get romanVariants() {
    return this.variants
      .filter((variant) => !variant.includes('italic'))
      .map((variant) => variant.replace('regular', '400'))
      .sort()
  }

  get italicVariants() {
    return this.variants
      .filter((variant) => variant.includes('italic'))
      .map((variant) => {
        if (variant === 'italic') {
          return '400'
        }

        return variant.replace('italic', '')
      })
      .sort()
  }

  get italicWeights() {
    return this.italicVariants.map((variant) => parseInt(variant) as FontWeightType)
  }

  get weights() {
    return this.romanVariants.map((variant) => parseInt(variant) as FontWeightType)
  }

  get url() {
    return this.styles
  }

  setLoadingState = (loadingState: LoadingState) => {
    this.loadingState = loadingState
  }

  load = async () => {
    if (this.loadingState === 'loaded' || this.loadingState === 'loading') {
      return this.loadingPromise
    }

    this.setLoadingState('loading')
    const testString = this.family === 'Noto Color Emoji' ? '😀' : null
    const romanVariants = this.romanVariants.map((variant) =>
      new FontFaceObserver(this.family, { weight: parseInt(variant) }).load(testString, FONT_LOADING_TIMEOUT),
    )
    const italicVariants = this.italicVariants.map((variant) =>
      new FontFaceObserver(this.family, { weight: parseInt(variant), style: 'italic' }).load(
        testString,
        FONT_LOADING_TIMEOUT,
      ),
    )
    this.loadingPromise = Promise.all([...romanVariants, ...italicVariants])
      .then(() => this.setLoadingState('loaded'))
      .catch((e) => {
        reportError(e)
        this.setLoadingState('idle')
      })
    return this.loadingPromise
  }

  analyze = async ({ weight, italic }: { weight: number; italic: boolean }) => {
    const fileKey = this.getFileKey({ weight, italic })
    const fileUrl = this.files[fileKey]
    if (this.analyses.has(fileUrl)) {
      return this.analyses.get(fileUrl)
    }
    const analysis = await opentype.load(this.files[fileKey])
    this.analyses.set(fileUrl, analysis)
    return analysis
  }

  getFileKey = ({ weight, italic }: { weight: number; italic: boolean }) => {
    const hasItalic = this.italicVariants.length > 0
    if (italic && hasItalic && weight === 400) {
      return 'italic'
    }

    if (weight === 400) {
      return 'regular'
    }

    return `${weight}${italic && hasItalic ? 'italic' : ''}`
  }
}
